import {
  VStack,
  HStack,
  Text,
  Box,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { FaCirclePlus } from 'react-icons/fa6';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider';
import { BenefitCard, BenefitCardProps } from './BenefitCard';

export interface Benefit {
  id?: string;
  title: string;
  image: string;
  category: string;
  active?: boolean;
  disabled?: boolean;
  allowEdit?: boolean;
  url?: string;
  icon?: string;
}

export interface BenefitCategoryProps {
  category: {
    category: string;
    benefits: Benefit[];
  };
  onBenefitSelect: (benefit: Benefit, event?: React.MouseEvent) => void;
  onBenefitEdit?: (benefit: Benefit) => void;
  onBenefitDelete?: (benefit: Benefit) => void;
  onBenefitToggle?: (benefit: Benefit) => void;
  onAddNew?: (category: string) => void;
  showAddButton?: boolean;
  cardProps?: Partial<BenefitCardProps> | ((benefit: Benefit) => Partial<BenefitCardProps>);
}

export const BenefitCategoryPanel = ({
  category,
  onBenefitSelect,
  onBenefitEdit,
  onBenefitDelete,
  onBenefitToggle,
  onAddNew,
  showAddButton = false,
  cardProps = {},
}: BenefitCategoryProps) => {
  const { colors } = useTheme();

  const getImageUrl = (benefit: Benefit) => {
    if (typeof benefit.image === 'object' && benefit.image) {
      return URL.createObjectURL(benefit.image as File);
    }
    return benefit.image || benefit.icon || '';
  };

  return (
    <HStack flexWrap="wrap" spacing={4} marginTop={4}>
      {category.benefits.map((benefit) => {
        const resolvedCardProps = typeof cardProps === 'function' ? cardProps(benefit) : cardProps;
        return (
          <BenefitCard
            key={benefit.title}
            title={benefit.title}
            image={getImageUrl(benefit)}
            selected={benefit.active}
            disabled={!benefit.active}
            color={colors.primaryColor}
            canEdit={benefit.allowEdit !== false}
            onClick={(event) => onBenefitSelect(benefit, event)}
            onEdit={onBenefitEdit ? () => onBenefitEdit(benefit) : undefined}
            onDelete={onBenefitDelete ? () => onBenefitDelete(benefit) : undefined}
            onToggle={onBenefitToggle ? () => onBenefitToggle(benefit) : undefined}
            {...resolvedCardProps}
          />
        );
      })}

      {showAddButton && onAddNew && (
        <VStack marginTop="auto" alignItems="center" position="relative">
          <Box
            backgroundColor={colors.primaryColor}
            cursor="pointer"
            borderRadius="10px"
            boxShadow="rgba(0, 0, 0, 0.1) 4px 4px 10px 0px"
            onClick={(e) => {
              e.stopPropagation();
              onAddNew(category.category);
            }}
            padding={4}
            alignItems="center"
            justifyContent="center"
            display="flex"
            gap={2}
            height="100px"
            width="100px"
            color="white"
            transition="all 0.2s"
            _hover={{ transform: 'translateY(-2px)' }}
          >
            <VStack spacing={2}>
              <FaCirclePlus size="24px" />
              <Text fontSize="12px" textAlign="center">
                Adicionar
              </Text>
            </VStack>
          </Box>
        </VStack>
      )}
    </HStack>
  );
};

export const BenefitCategory = ({
  category,
  onBenefitSelect,
  onBenefitEdit,
  onBenefitDelete,
  onBenefitToggle,
  onAddNew,
  showAddButton = false,
  cardProps = {},
}: BenefitCategoryProps) => {
  return (
    <AccordionItem>
      <AccordionButton>
        <HStack align="center" justifyContent="space-between" width="100%">
          <Text fontWeight="bold" fontSize="md" marginTop={4}>
            {category.category}
          </Text>
          <AccordionIcon />
        </HStack>
      </AccordionButton>
      
      <AccordionPanel pb={4}>
        <BenefitCategoryPanel
          category={category}
          onBenefitSelect={onBenefitSelect}
          onBenefitEdit={onBenefitEdit}
          onBenefitDelete={onBenefitDelete}
          onBenefitToggle={onBenefitToggle}
          onAddNew={onAddNew}
          showAddButton={showAddButton}
          cardProps={cardProps}
        />
      </AccordionPanel>
    </AccordionItem>
  );
};

interface BenefitCategorySimpleProps {
  title: string;
  benefits: Benefit[];
  onBenefitSelect: (benefit: Benefit, event?: React.MouseEvent) => void;
  cardProps?: Partial<BenefitCardProps>;
}

export const BenefitCategorySimple = ({
  title,
  benefits,
  onBenefitSelect,
  cardProps = {},
}: BenefitCategorySimpleProps) => {
  const { colors } = useTheme();

  const getImageUrl = (benefit: Benefit) => {
    if (typeof benefit.image === 'object' && benefit.image) {
      return URL.createObjectURL(benefit.image as File);
    }
    return benefit.image || benefit.icon || '';
  };

  return (
    <VStack margin="auto" gap={0}>
      <Text fontSize="md" marginTop={4} fontWeight="semibold">
        {title}
      </Text>
      <HStack flexWrap="wrap" spacing={5} marginTop={4}>
        {benefits.map((benefit) => (
          <BenefitCard
            key={benefit.title}
            title={benefit.title}
            image={getImageUrl(benefit)}
            selected={benefit.active}
            disabled={!benefit.active}
            color={colors.primaryColor}
            onClick={(event) => onBenefitSelect(benefit, event)}
            {...cardProps}
          />
        ))}
      </HStack>
    </VStack>
  );
}; 